import { QueryHookOptions } from "@apollo/client"
import AvatarStack from "@pathwright/ui/src/components/avatar/AvatarStack"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import { flattenEdges } from "@pathwright/utils"
import classnames from "classnames"
import { TFunction } from "i18next"
import PropTypes from "prop-types"
import { useMemo } from "react"
import styled from "styled-components"
import {
  InboxReviewersQuery,
  InboxReviewersQueryVariables,
  UserFragment,
  useInboxReviewersQuery
} from "../../api/generated"
import { TreeNode } from "../state/tree/path-tree"

export const useInboxReviewers = (
  pathId: number,
  options?: QueryHookOptions<InboxReviewersQuery, InboxReviewersQueryVariables>
) => {
  const query = useInboxReviewersQuery({
    variables: { pathId: pathId },
    ...options
  })

  const reviewers = useMemo(() => {
    if (query.data) {
      return flattenEdges(query.data.inboxReviewers) || []
    }
    return []
  }, [query.data])

  return { query, reviewers }
}

export type GetLabelFunc = (
  t: TFunction,
  args: { count: number; limit: number }
) => string | null

export const getReviewersLabel: GetLabelFunc = (t, { count, limit }) => {
  const remainder = count - limit

  if (remainder > 0) {
    return t("+{{ count }} reviewer", {
      defaultValue_plural: "{{ count }} reviewers",
      count: remainder
    })
  }

  return t("{{ count }} reviewer", {
    defaultValue_plural: "{{ count }} reviewers",
    count
  })
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  > span:last-child {
    margin-left: 0.4em;
  }
`

const Reviewers = ({
  className,
  item,
  limit,
  getLabel,
  getTooltip,
  ...rest
}: {
  className?: string
  item: TreeNode<"completion">
  limit: number
  getLabel: GetLabelFunc
  getTooltip?: ((user?: UserFragment) => string) | null
}) => {
  const { query, reviewers } = useInboxReviewers(item.data.path_id)
  const { t } = useTranslate()
  const label = getLabel(t, { count: reviewers?.length || 0, limit })

  if (query.loading || !reviewers?.length) return null

  return (
    <Container className={classnames("Reviewers", className)}>
      <AvatarStack
        users={reviewers.map((reviewer) => reviewer.user)}
        size="1.4em"
        itemOffset="-0.2em"
        limit={limit}
        reverse
        getTooltip={(item: UserFragment) => item.display_name}
        {...rest}
      />
      {!!label && <span>{label}</span>}
    </Container>
  )
}

Reviewers.displayName = "Reviewers"

Reviewers.propTypes = {
  item: PropTypes.shape({
    data: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired
  }).isRequired,
  limit: PropTypes.number,
  getLabel: PropTypes.func.isRequired
}

Reviewers.defaultProps = {
  item: null,
  limit: 3,
  getLabel: getReviewersLabel
}

export default Reviewers
